import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(15);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const targetDate = new Date('2026-01-04T00:00:00').getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 text-white font-['Inter',sans-serif]">
      {/* Floating Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/a66a38f581526f9f97f13d7c8b5f2655.png" 
              alt="Mentoria 10X con iA" 
              className="h-8 sm:h-10 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#modulos" className="text-sm text-gray-300 hover:text-orange-400 transition-colors cursor-pointer">Programa</a>
            <a href="#planes" className="text-sm text-gray-300 hover:text-orange-400 transition-colors cursor-pointer">Inversión</a>
            <a href="#faq" className="text-sm text-gray-300 hover:text-orange-400 transition-colors cursor-pointer">FAQ</a>
            <Link to="/inscripcion" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black px-6 py-2.5 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap">
              Inscríbete
            </Link>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-white`}></i>
          </button>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
            <div className="px-4 py-6 space-y-4">
              <a href="#modulos" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-orange-400 transition-colors cursor-pointer py-2">Programa</a>
              <a href="#planes" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-orange-400 transition-colors cursor-pointer py-2">Inversión</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-orange-400 transition-colors cursor-pointer py-2">FAQ</a>
              <Link to="/inscripcion" onClick={() => setMobileMenuOpen(false)} className="block bg-gradient-to-r from-orange-500 to-amber-500 text-black px-6 py-3 rounded-full font-semibold text-center cursor-pointer">
                Inscríbete
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 px-4 sm:px-6">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-amber-500/10"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto py-12 sm:py-20 text-center">
          <div className="space-y-8 sm:space-y-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <p className="text-orange-400 text-xs sm:text-sm font-semibold tracking-wider">ENERO 2026 • EN VIVO</p>
            </div>

            {/* Main Title */}
            <div className="space-y-4 sm:space-y-6">
              <h1 className="text-5xl sm:text-7xl md:text-9xl font-black leading-none">
                <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                  MENTORIA
                </span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 bg-clip-text text-transparent animate-gradient">
                  10X con iA
                </span>
              </h1>
              <p className="text-lg sm:text-2xl md:text-3xl font-light text-gray-300 max-w-4xl mx-auto leading-relaxed px-4">
                Domina la <strong className="text-white font-semibold">Inteligencia Artificial</strong> aplicada al Marketing Digital
              </p>
            </div>

            {/* Countdown Timer */}
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm">
                <i className="ri-calendar-event-line text-amber-400"></i>
                <p className="text-amber-400 text-xs sm:text-sm font-semibold tracking-wider">Inicia Domingo 4 de Enero 2026</p>
              </div>
              
              <div className="bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12">
                <p className="text-gray-400 text-xs sm:text-sm md:text-base mb-4 sm:mb-6 tracking-wider">DISPONIBLE POR TIEMPO LIMITADO</p>
                <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-8">
                  <div className="space-y-2">
                    <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 backdrop-blur-sm">
                      <div className="text-3xl sm:text-5xl md:text-7xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                        {String(timeLeft.days).padStart(2, '0')}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm md:text-base font-semibold">Días</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 backdrop-blur-sm">
                      <div className="text-3xl sm:text-5xl md:text-7xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                        {String(timeLeft.hours).padStart(2, '0')}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm md:text-base font-semibold">Horas</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 backdrop-blur-sm">
                      <div className="text-3xl sm:text-5xl md:text-7xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                        {String(timeLeft.minutes).padStart(2, '0')}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm md:text-base font-semibold">Minutos</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 backdrop-blur-sm">
                      <div className="text-3xl sm:text-5xl md:text-7xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                        {String(timeLeft.seconds).padStart(2, '0')}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm md:text-base font-semibold">Segundos</p>
                  </div>
                </div>
              </div>

              {/* Video Section */}
              <div className="bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 overflow-hidden">
                <div className="aspect-video w-full rounded-xl sm:rounded-2xl overflow-hidden">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/qV-g1fQfWic"
                    title="Mentoria 10X con IA"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <Link
                to="/inscripcion"
                className="group inline-flex items-center gap-3 sm:gap-4 bg-gradient-to-r from-orange-500 to-amber-500 text-black px-8 sm:px-12 py-4 sm:py-6 rounded-full font-bold text-lg sm:text-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                Inscríbete Ahora
                <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            {/* Progress Bar */}
            <div className="max-w-lg mx-auto mt-12 sm:mt-16 space-y-4 px-4">
              <div className="relative w-full bg-white/5 rounded-full h-3 overflow-hidden backdrop-blur-sm border border-white/10">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-400">
                <span className="font-bold text-orange-400 text-base sm:text-lg">{progress}% de plazas vendidas</span> del lote promocional
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto pt-8 sm:pt-12 px-4">
              <div className="space-y-2">
                <div className="text-3xl sm:text-4xl font-bold text-orange-400">16</div>
                <div className="text-xs sm:text-sm text-gray-400">Sesiones Intensivas</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl sm:text-4xl font-bold text-orange-400">30h+</div>
                <div className="text-xs sm:text-sm text-gray-400">De Contenido en Vivo</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl sm:text-4xl font-bold text-orange-400">3</div>
                <div className="text-xs sm:text-sm text-gray-400">Meses de Soporte</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <i className="ri-arrow-down-line text-3xl text-orange-400"></i>
        </div>
      </section>

      {/* 8 Sessions */}
      <section className="py-16 sm:py-24 md:py-32 relative overflow-hidden px-4 sm:px-6" id="modulos">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm mb-6 sm:mb-8">
              <p className="text-orange-400 text-xs sm:text-sm font-semibold tracking-wider">PROGRAMA COMPLETO</p>
            </div>
            <h2 className="text-4xl sm:text-6xl md:text-7xl font-black mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                16 Sesiones
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                Intensivas
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              Más de 30 horas de contenido en vivo para dominar la IA en marketing digital
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { 
                week: 'SEMANA 1', 
                title: 'MENTALIDAD 10X + DOMINIO PROFESIONAL DE LOS LLM', 
                value: '$997',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/a3e27326b0468eddad81a468b926272d.png',
                color: 'from-orange-500 to-amber-500'
              },
              { 
                week: 'SEMANA 2', 
                title: 'REINVENTA TU MARCA PERSONAL CON IA', 
                value: '$497',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/fc7682fbd7e1dca4f5c2c8a928588c28.png',
                color: 'from-amber-500 to-yellow-500'
              },
              { 
                week: 'SEMANA 3', 
                title: 'VIDEOS CON IA', 
                value: '$197',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/a9eaf6e23ef1d167c389c5ddbf6590eb.png',
                color: 'from-yellow-500 to-orange-500'
              },
              { 
                week: 'SEMANA 4', 
                title: 'CLONES DIGITALES', 
                value: '$197',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/fe9a7d54e14131975d310023266d3184.png',
                color: 'from-orange-600 to-amber-600'
              },
              { 
                week: 'SEMANA 5', 
                title: 'WEBPAGE Y LANDING PAGE CON IA', 
                value: '$197',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/e45e087b372a5988c6e9f2e7d3df282e.png',
                color: 'from-amber-600 to-orange-500'
              },
              { 
                week: 'SEMANA 6', 
                title: 'ESCRITOR EN UNA SEMANA CON IA (LIBRO FÍSICO Y DIGITAL)', 
                value: '$997',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/4229e9174e68188eadfa42715db4b5c8.png',
                color: 'from-orange-500 to-yellow-600'
              },
              { 
                week: 'SEMANA 7', 
                title: 'INTEGRACIÓN TOTAL + PRESENTACIÓN DE PROYECTOS FINALES', 
                value: 'INCLUIDO',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/c33082bbc557b6be570e7cf4c9743619.png',
                color: 'from-yellow-600 to-amber-500'
              },
              { 
                week: 'SESIÓN #8', 
                title: 'BONUS ESPECIAL: AGENTE TELEFÓNICO IA', 
                value: '$499',
                image: 'https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/1b44c80b29d8427c33264808d75e38af.png',
                color: 'from-amber-500 to-orange-500'
              },
            ].map((item, index) => (
              <div key={index} className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-white/30 transition-all cursor-pointer backdrop-blur-sm hover:transform hover:scale-105">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    src={item.image}
                    alt={item.title}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                </div>
                <div className="p-6 space-y-3">
                  <div className={`inline-block bg-gradient-to-r ${item.color} text-white px-4 py-1.5 rounded-full text-xs font-bold tracking-wider`}>
                    {item.week}
                  </div>
                  <h3 className="text-base font-bold text-white leading-tight min-h-[3rem]">{item.title}</h3>
                  <p className="text-orange-400 font-bold text-sm">VALOR: {item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Total Value */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="inline-block bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-2xl px-8 sm:px-12 py-4 sm:py-6 backdrop-blur-sm">
              <p className="text-gray-400 text-xs sm:text-sm mb-2">VALOR TOTAL DEL PROGRAMA</p>
              <p className="text-3xl sm:text-5xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">$3,581</p>
            </div>
          </div>
        </div>
      </section>

      {/* Incluye Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-br from-slate-900 via-orange-950/30 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-6 py-3 backdrop-blur-sm mb-8">
              <i className="ri-gift-line text-orange-400"></i>
              <p className="text-orange-400 text-sm font-semibold tracking-wider">TODO INCLUIDO</p>
            </div>
            <h2 className="text-6xl md:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Además
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                Recibes
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-file-text-line',
                title: 'Prompts Exclusivos',
                description: 'Biblioteca completa de prompts profesionales para cada herramienta de IA',
                gradient: 'from-orange-500 to-amber-500'
              },
              {
                icon: 'ri-robot-line',
                title: "GPT's Personalizados",
                description: 'GPTs que hacen el 90% del trabajo por ti, listos para usar',
                gradient: 'from-amber-500 to-yellow-500'
              },
              {
                icon: 'ri-gemini-line',
                title: 'Gemas de Gemini',
                description: 'Gemas optimizadas que automatizan tus procesos de marketing',
                gradient: 'from-yellow-500 to-orange-500'
              },
              {
                icon: 'ri-whatsapp-line',
                title: 'Grupo de WhatsApp',
                description: 'Soporte directo y comunidad activa por 3 meses completos',
                gradient: 'from-orange-600 to-amber-600'
              },
              {
                icon: 'ri-video-line',
                title: 'Grabaciones Completas',
                description: 'Acceso a todas las sesiones grabadas durante 3 meses',
                gradient: 'from-amber-600 to-orange-500'
              },
            ].map((item, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-white/30 transition-all cursor-pointer backdrop-blur-sm p-8 hover:transform hover:scale-105"
              >
                <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AI Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-amber-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-6 py-3 backdrop-blur-sm">
                <p className="text-orange-400 text-sm font-semibold tracking-wider">¿POR QUÉ AHORA?</p>
              </div>
              <h2 className="text-5xl md:text-6xl font-black leading-tight">
                <span className="text-white">La IA está</span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                  Revolucionando
                </span>
                <br />
                <span className="text-white">el Marketing</span>
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed">
                En 2026, los profesionales que dominen la IA tendrán una ventaja competitiva sin precedentes. Este programa te prepara para liderar esta transformación.
              </p>
              <div className="space-y-4">
                {[
                  'Automatiza tareas repetitivas y enfócate en estrategia',
                  'Crea contenido de calidad profesional en minutos',
                  'Multiplica tu productividad por 10x',
                  'Diferénciate de tu competencia'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex-shrink-0 mt-1">
                      <i className="ri-check-line text-sm text-white"></i>
                    </div>
                    <p className="text-gray-300 text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/3943f602ac15221be7467c42f8b8e8d6.jpg"
                alt="AI Marketing Professional"
                className="relative rounded-3xl w-full h-auto object-cover border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-br from-slate-900 via-amber-950/30 to-slate-900">
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-3 backdrop-blur-sm mb-8">
              <i className="ri-calendar-line text-amber-400"></i>
              <p className="text-amber-400 text-sm font-semibold tracking-wider">CRONOGRAMA</p>
            </div>
            <h2 className="text-6xl md:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Sesiones
              </span>
              <br />
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                En Vivo
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-6">
              Cada domingo de 6:00 PM a 8:00 PM (hora de California)
            </p>
            <div className="inline-block bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-full px-6 py-3 backdrop-blur-sm">
              <p className="text-green-400 text-sm font-semibold">
                <i className="ri-calendar-check-line mr-2"></i>
                Cada miércoles: Sesión de entrega de trabajos y preguntas y respuestas
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {[
              { date: 'Enero 4', time: '19:00 - 21:00', session: 'Semana 1', title: 'Mentalidad 10X + Dominio Profesional de los LLM', value: '$997' },
              { date: 'Enero 11', time: '19:00 - 21:00', session: 'Semana 2', title: 'Reinventa tu Marca Personal con IA', value: '$497' },
              { date: 'Enero 18', time: '19:00 - 21:00', session: 'Semana 3', title: 'Videos con IA', value: '$197' },
              { date: 'Enero 25', time: '19:00 - 21:00', session: 'Semana 4', title: 'Clones Digitales', value: '$197' },
              { date: 'Febrero 1', time: '19:00 - 21:00', session: 'Semana 5', title: 'Webpage y Landing Page con IA', value: '$197' },
              { date: 'Febrero 8', time: '19:00 - 21:00', session: 'Semana 6', title: 'Escritor en una Semana con IA (Libro Físico y Digital)', value: '$997' },
              { date: 'Febrero 15', time: '19:00 - 21:00', session: 'Semana 7', title: 'Integración Total + Presentación de Proyectos Finales', value: 'INCLUIDO' },
              { date: 'Febrero 22', time: '19:00 - 21:00', session: 'Sesión #8', title: 'Bonus Especial: Agente Telefónico IA', value: '$499' },
            ].map((item, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-white/5 to-white/[0.02] border border-white/10 hover:border-amber-500/50 transition-all cursor-pointer backdrop-blur-sm p-6 hover:transform hover:translate-x-2"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-center gap-6 flex-1">
                    <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-2xl font-black text-white">{item.date.split(' ')[1]}</div>
                        <div className="text-xs text-white/80 font-semibold">{item.date.split(' ')[0].toUpperCase()}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-amber-400 text-sm font-bold mb-1">{item.session}</div>
                      <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                      <div className="flex items-center gap-4 text-gray-400 text-sm">
                        <div className="flex items-center gap-2">
                          <i className="ri-time-line"></i>
                          <span>{item.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-price-tag-3-line"></i>
                          <span className="text-orange-400 font-semibold">Valor: {item.value}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line text-2xl text-gray-600 group-hover:text-amber-400 group-hover:translate-x-1 transition-all"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-24 md:py-32 relative overflow-hidden px-4 sm:px-6" id="planes">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm mb-6 sm:mb-8">
              <i className="ri-price-tag-3-line text-orange-400"></i>
              <p className="text-orange-400 text-xs sm:text-sm font-semibold tracking-wider">INVERSIÓN</p>
            </div>
            <h2 className="text-4xl sm:text-6xl md:text-7xl font-black mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Elige tu
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                Plan
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              Selecciona la opción de pago que mejor se adapte a ti
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Plan de 4 Pagos */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/20 backdrop-blur-xl p-10">
              <div className="relative z-10 space-y-8">
                {/* Badge */}
                <div className="flex justify-center">
                  <div className="bg-gradient-to-r from-white/20 to-white/10 text-white px-6 py-2 rounded-full text-sm font-bold border border-white/30">
                    PLAN FLEXIBLE
                  </div>
                </div>

                {/* Price */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-2xl text-gray-500 line-through">$3,581</span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-7xl font-black text-white">
                      $97
                    </div>
                    <p className="text-gray-300 text-lg">por semana durante 4 semanas</p>
                    <p className="text-sm text-gray-400">Total: $388</p>
                  </div>
                </div>

                {/* Progress */}
                <div className="space-y-3">
                  <div className="relative w-full bg-white/10 rounded-full h-4 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-400">
                    Solo quedan <span className="font-bold text-white text-lg">{100 - progress}%</span> de plazas disponibles
                  </p>
                </div>

                {/* CTA Button */}
                <Link
                  to="/inscripcion"
                  className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-6 rounded-2xl font-bold text-xl hover:shadow-2xl hover:shadow-gray-500/50 transition-all whitespace-nowrap cursor-pointer transform hover:scale-105"
                >
                  <i className="ri-user-add-line text-2xl"></i>
                  Inscríbete Ahora
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
                </Link>

                {/* Features */}
                <div className="space-y-3 pt-6">
                  {[
                    '16 Sesiones en vivo',
                    'Grabaciones por 3 meses',
                    'Grupo de WhatsApp',
                    'Prompts exclusivos',
                    "GPT's personalizados",
                    'Gemas de Gemini',
                    'Soporte continuo',
                    'Certificado oficial'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-gray-400 text-xl"></i>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Plan de 1 Pago - DESTACADO */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/10 to-white/5 border-2 border-orange-500/50 backdrop-blur-xl p-10">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 opacity-50"></div>
              
              <div className="relative z-10 space-y-8">
                {/* Badge */}
                <div className="flex justify-center">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-black px-6 py-2 rounded-full text-sm font-bold">
                    🔥 MEJOR PRECIO
                  </div>
                </div>

                {/* Price */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-2xl text-gray-500 line-through">$3,581</span>
                  </div>
                  <div className="space-y-2">
                    <div className="text-7xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                      $297
                    </div>
                    <p className="text-gray-300 text-lg">Pago único • Acceso completo</p>
                    <div className="inline-block bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-full px-4 py-2 mt-2">
                      <p className="text-green-400 font-semibold text-sm">Ahorras $91</p>
                    </div>
                  </div>
                </div>

                {/* Progress */}
                <div className="space-y-3">
                  <div className="relative w-full bg-white/10 rounded-full h-4 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-400">
                    Solo quedan <span className="font-bold text-orange-400 text-lg">{100 - progress}%</span> de plazas disponibles
                  </p>
                </div>

                {/* CTA Button */}
                <Link
                  to="/inscripcion"
                  className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-orange-500 to-amber-500 text-black px-8 py-6 rounded-2xl font-bold text-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all whitespace-nowrap cursor-pointer transform hover:scale-105"
                >
                  <i className="ri-user-add-line text-2xl"></i>
                  Inscríbete Ahora
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
                </Link>

                {/* Features */}
                <div className="space-y-3 pt-6">
                  {[
                    '16 Sesiones en vivo',
                    'Grabaciones por 3 meses',
                    'Grupo de WhatsApp',
                    'Prompts exclusivos',
                    "GPT's personalizados",
                    'Gemas de Gemini',
                    'Soporte continuo',
                    'Certificado oficial'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-orange-400 text-xl"></i>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Guarantee Badge */}
          <div className="flex justify-center mt-8 sm:mt-12">
            <a
              href="https://wa.link/88xra1"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-green-500/50 transition-all cursor-pointer transform hover:scale-105"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
              Contactar por WhatsApp
              <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Mentor Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-br from-slate-900 via-orange-950/30 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/220d3b4840a08f0e469fe36dba2734fe.png"
                  alt="Edgardo Moreno"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="space-y-8 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-6 py-3 backdrop-blur-sm">
                <i className="ri-user-star-line text-orange-400"></i>
                <p className="text-orange-400 text-sm font-semibold tracking-wider">TU MENTOR</p>
              </div>
              <h2 className="text-5xl md:text-6xl font-black leading-tight">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Edgardo
                </span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                  Moreno
                </span>
              </h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Licenciado en Comunicaciones, con especialidad en Mercadeo, Mercadeo Digital e Inteligencia Artificial aplicada al Marketing, con más de 25 años de experiencia transformando negocios a través de la tecnología.
                </p>
                <p>
                  Ha capacitado a más de 8,000 profesionales en Latinoamérica, ayudándoles a multiplicar su productividad y resultados utilizando herramientas de IA de última generación.
                </p>
                <p>
                  Creador de metodologías propias que combinan estrategia de marketing, automatización y IA para lograr resultados extraordinarios en tiempo récord.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center space-y-2">
                  <div className="text-4xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">25+</div>
                  <div className="text-sm text-gray-400">Años de Experiencia</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-4xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">8K+</div>
                  <div className="text-sm text-gray-400">Alumnos</div>
                </div>
                <div className="text-center space-y-2">
                  <div className="text-4xl font-black bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">20+</div>
                  <div className="text-sm text-gray-400">Países</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 relative overflow-hidden" id="faq">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-3 backdrop-blur-sm mb-8">
              <i className="ri-question-line text-amber-400"></i>
              <p className="text-amber-400 text-sm font-semibold tracking-wider">PREGUNTAS FRECUENTES</p>
            </div>
            <h2 className="text-6xl md:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                ¿Tienes
              </span>
              <br />
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                Dudas?
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: '¿Necesito experiencia previa en IA?',
                answer: 'No, el programa está diseñado para todos los niveles. Comenzamos desde cero y te llevamos paso a paso hasta dominar las herramientas más avanzadas de IA aplicadas al marketing digital.'
              },
              {
                question: '¿Las sesiones quedan grabadas?',
                answer: 'Sí, todas las sesiones en vivo quedan grabadas y tendrás acceso a ellas durante 6 meses completos. Podrás verlas cuantas veces necesites y a tu propio ritmo.'
              },
              {
                question: '¿Qué herramientas de IA vamos a usar?',
                answer: 'Trabajaremos con ChatGPT, Claude, Gemini, Midjourney, RunwayML, ElevenLabs, y muchas otras herramientas de última generación. Te enseñaremos a dominar cada una de ellas.'
              },
              {
                question: '¿Cuánto tiempo debo dedicar por semana?',
                answer: 'Recomendamos dedicar 3-4 horas por semana: 2 horas para la sesión en vivo y 1-2 horas para practicar lo aprendido. El programa está diseñado para personas ocupadas.'
              },
              {
                question: '¿Hay garantía de devolución?',
                answer: 'Sí, ofrecemos garantía de 7 días. Si después de la primera sesión sientes que el programa no es para ti, te devolvemos el 100% de tu inversión sin preguntas.'
              },
              {
                question: '¿Cómo funciona la sesión privada 1 a 1?',
                answer: 'Los primeros 30 alumnos inscritos recibirán una sesión privada de 1 hora con Edgardo Moreno (valor $300). En esta sesión podrás resolver dudas específicas de tu negocio y recibir asesoría personalizada.'
              },
              {
                question: '¿Qué pasa si me pierdo una sesión en vivo?',
                answer: 'No hay problema. Todas las sesiones quedan grabadas y disponibles en la plataforma. Además, el grupo de WhatsApp te mantendrá al día con todo lo importante.'
              },
              {
                question: '¿Recibiré certificado al finalizar?',
                answer: 'Sí, al completar el programa y presentar tu proyecto final recibirás un certificado digital de finalización que podrás compartir en LinkedIn y tu portafolio profesional.'
              }
            ].map((item, index) => (
              <details key={index} className="group bg-gradient-to-r from-white/5 to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm hover:border-amber-500/30 transition-all">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-xl font-bold text-white pr-4">{item.question}</h3>
                  <i className="ri-add-line text-2xl text-amber-400 group-open:rotate-45 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-400 leading-relaxed text-lg">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-br from-orange-950 via-slate-900 to-amber-950">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <div className="space-y-10">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-6 py-3 backdrop-blur-sm">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <p className="text-orange-400 text-sm font-semibold tracking-wider">ÚLTIMAS PLAZAS DISPONIBLES</p>
            </div>
            
            <h2 className="text-6xl md:text-8xl font-black leading-tight">
              <span className="text-white">Transforma tu</span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                Carrera Profesional
              </span>
              <br />
              <span className="text-white">en 2026</span>
            </h2>

            <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Únete a la revolución de la IA en marketing digital. Las plazas se están agotando rápidamente.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8">
              <Link
                to="/inscripcion"
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 text-black px-10 py-5 rounded-full font-bold text-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                <i className="ri-user-add-line text-2xl"></i>
                Inscríbete Ahora
                <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <a
                href="#faq"
                className="inline-flex items-center gap-2 bg-white/5 border border-white/20 text-white px-10 py-5 rounded-full font-semibold text-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                Ver Preguntas Frecuentes
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto pt-16">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <i className="ri-shield-check-line text-4xl text-orange-400 mb-4"></i>
                <h3 className="text-lg font-bold text-white mb-2">Garantía 7 días</h3>
                <p className="text-gray-400 text-sm">Devolución completa si no estás satisfecho</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <i className="ri-group-line text-4xl text-amber-400 mb-4"></i>
                <h3 className="text-lg font-bold text-white mb-2">Comunidad activa</h3>
                <p className="text-gray-400 text-sm">Soporte y networking por 3 meses</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                <i className="ri-award-line text-4xl text-orange-400 mb-4"></i>
                <h3 className="text-lg font-bold text-white mb-2">Certificado oficial</h3>
                <p className="text-gray-400 text-sm">Valida tus nuevas habilidades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/a66a38f581526f9f97f13d7c8b5f2655.png" 
                alt="Mentoria 10X con iA" 
                className="h-12 w-auto"
              />
              <p className="text-gray-400 leading-relaxed">
                Mentoria profesional en Inteligencia Artificial aplicada al Marketing Digital.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer">
                  <i className="ri-instagram-line text-lg text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer">
                  <i className="ri-youtube-fill text-lg text-white"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6">Programa</h4>
              <ul className="space-y-3">
                <li><a href="#modulos" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">8 Sesiones</a></li>
                <li><a href="#planes" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">Inversión</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6">Recursos</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">Casos de Éxito</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">Testimonios</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6">Contacto</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-400">
                  <i className="ri-whatsapp-line text-orange-400"></i>
                  <span>562-674-5958</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <i className="ri-mail-line text-orange-400"></i>
                  <span>info@mentoria10x.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs sm:text-sm text-center md:text-left">© 2026 Mentoria 10X con iA. Todos los derechos reservados.</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <a href="#" className="text-gray-500 hover:text-orange-400 text-xs sm:text-sm transition-colors cursor-pointer">Términos y Condiciones</a>
              <a href="#" className="text-gray-500 hover:text-orange-400 text-xs sm:text-sm transition-colors cursor-pointer">Política de Privacidad</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-orange-400 text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}